import React, { useEffect, useMemo, useState } from 'react'
import {
  Box, Chip, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material'
import TrendingUpIcon   from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import SpeedIcon        from '@mui/icons-material/Speed'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  Bar, BarChart, CartesianGrid, Cell,
  ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts'
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_MID, WARNING, WARNING_LIGHT, GREEN, RED, matchRow, scopeTotals, displayName, Kpi, Pct } from './index'
import { fetchTrendDaily } from './engine'

/* ─── Formatters ─────────────────────────────────────────────────────────── */
const nf = n => (n == null || isNaN(n)) ? '—' : Math.round(n).toLocaleString('en-IN')

/* ─── UI atoms ───────────────────────────────────────────────────────────── */
function SegBadge({ seg }) {
  return (
    <Box component='span' sx={{
      fontSize: '0.6rem', fontWeight: 800, px: 0.8, py: 0.25, borderRadius: 0.5, letterSpacing: 0.3,
      background: seg === 'ENT' ? PRIMARY_LIGHT : WARNING_LIGHT,
      color:      seg === 'ENT' ? PRIMARY       : WARNING,
    }}>
      {seg}
    </Box>
  )
}

/* ─── KPI card ───────────────────────────────────────────────────────────── */
/* ─── Section card ───────────────────────────────────────────────────────── */
function SCard({ title, badge, cnt, children }) {
  return (
    <Box sx={{ background: '#fff', borderRadius: '12px', border: '1px solid rgba(145,158,171,0.24)', boxShadow: 'none', overflow: 'hidden', mb: 2 }}>
      {(title || badge != null) && (
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid rgba(145,158,171,0.24)', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a2e', flex: 1 }}>{title}</Typography>
          {badge != null && (
            <Chip label={badge} size='small' sx={{
              height: 20, fontSize: '0.62rem', fontWeight: 700, borderRadius: '6px',
              ...(typeof badge === 'number' && badge > 0 ? { background: '#FFE7D9', color: '#B72136' } : { background: PRIMARY_LIGHT, color: PRIMARY }),
            }} />
          )}
          {cnt && <Typography sx={{ fontSize: '0.7rem', color: '#bbb' }}>{cnt}</Typography>}
        </Box>
      )}
      {children}
    </Box>
  )
}

/* ─── Th ─────────────────────────────────────────────────────────────────── */
function Th({ children, col, sortCol, sortDir, onSort, right }) {
  const active = sortCol === col
  return (
    <TableCell align={right ? 'right' : 'left'} onClick={col ? () => onSort(col) : undefined} sx={{
      fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
      color: active ? PRIMARY : '#a0a0b8', background: '#F4F6F8', borderBottom: `2px solid ${PRIMARY_MID}`,
      py: 1.25, whiteSpace: 'nowrap', cursor: col ? 'pointer' : 'default', position: 'sticky', top: 0, zIndex: 2,
      '&:hover': col ? { color: PRIMARY } : {},
    }}>
      {children}{active && <span style={{ fontSize: 8, marginLeft: 3 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
    </TableCell>
  )
}

function useSortState(def = null) {
  const [sortCol, setSortCol] = useState(def)
  const [sortDir, setSortDir] = useState('desc')
  const toggleSort = col => { if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortCol(col); setSortDir('desc') } }
  const sorted = rows => !sortCol ? rows : [...rows].sort((a, b) => { const va = a[sortCol] ?? -Infinity, vb = b[sortCol] ?? -Infinity; return sortDir === 'asc' ? va - vb : vb - va })
  return { sortCol, sortDir, toggleSort, sorted, thP: { sortCol, sortDir, onSort: toggleSort } }
}

/* ─── DRR info card ──────────────────────────────────────────────────────── */
function DrrInfo({ t, S0, S1, S2, L0, L1, L2 }) {
  return (
    <Box sx={{ p: 2.5 }}>
      <Grid container spacing={1.25} sx={{ mb: 2 }}>
        {[{ l: `DRR ${S0}`, v: nf(t.drrApr), c: '#9a9ab0' }, { l: `DRR ${S1}`, v: nf(t.drrMay), c: '#555' }, { l: `DRR ${S2}`, v: nf(t.drrJun), c: PRIMARY }].map(({ l, v, c }) => (
          <Grid item xs={4} key={l}>
            <Box sx={{ background: '#F4F6F8', borderRadius: '10px', p: '12px 14px', textAlign: 'center', border: `1px solid ${PRIMARY_MID}` }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#9a9ab0' }}>{l}</Typography>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: c, mt: 0.4, letterSpacing: '-0.02em' }}>{v}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      {[
        [`${L1} → ${L2} (proj)`, <Pct v={t.gMJ} />],
        [`${L0} → ${L2} (proj)`, <Pct v={t.gAJ} />],
        ['Week-on-week',          <Pct v={t.wow} />],
        ['Active / inactive',     `${nf(t.active)} / ${nf(t.inactive)}`],
      ].map(([label, val], i) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.85, borderBottom: i < 3 ? '1px solid #f0ecf9' : 'none' }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#555' }}>{label}</Typography>
          <Typography component='div' sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{val}</Typography>
        </Box>
      ))}
    </Box>
  )
}

/* ─── Daily bar chart ────────────────────────────────────────────────────── */
function DailyBars({ data }) {
  if (!data?.length) return <Box sx={{ p: 4, textAlign: 'center', color: '#bbb', fontSize: '0.82rem' }}>Daily data loads with live data.</Box>
  return (
    <Box sx={{ px: 2, pb: 2, pt: 1 }}>
      <ResponsiveContainer width='100%' height={160}>
        <BarChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 0 }} barCategoryGap='30%'>
          <CartesianGrid strokeDasharray='3 3' stroke='rgba(145,158,171,0.24)' vertical={false} />
          <XAxis dataKey='label' tick={{ fontSize: 9, fill: '#a0a0b8' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 9, fill: '#a0a0b8' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v} width={36} />
          <RTooltip formatter={v => [nf(v), 'Shipments']} contentStyle={{ fontSize: 11, borderRadius: 10, border: `1px solid ${PRIMARY_MID}` }} />
          <Bar dataKey='total' radius={[5, 5, 0, 0]}>
            {data.map((_, i) => <Cell key={i} fill={i === data.length - 1 ? WARNING : PRIMARY} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <Typography sx={{ textAlign: 'right', fontSize: '0.67rem', color: '#bbb' }}>Latest day in orange</Typography>
    </Box>
  )
}

/* ─── Generic account table ──────────────────────────────────────────────── */
function AccTable({ rows, L0, L1, L2, S1, S2, maxRows = 50 }) {
  const { sortCol, sortDir, toggleSort, sorted, thP } = useSortState()
  const shown = sorted(rows).slice(0, maxRows)
  const tots  = shown.reduce((a, r) => { a.apr += r.apr || 0; a.may += r.may || 0; a.jun += r.jun || 0; a.proj += r.proj || 0; return a }, { apr: 0, may: 0, jun: 0, proj: 0 })
  if (!shown.length) return <Box sx={{ py: 4, textAlign: 'center', color: '#bbb', fontSize: '0.8rem' }}>No accounts match the filter.</Box>
  return (
    <TableContainer sx={{ maxHeight: 400 }}>
      <Table size='small' stickyHeader>
        <TableHead><TableRow>
          <Th {...thP} col='name' minW={160}>Account</Th>
          <Th>Seg</Th>
          <Th {...thP} col='kam'>KAM</Th>
          <Th {...thP} col='apr' right>{L0}</Th>
          <Th {...thP} col='may' right>{L1}</Th>
          <Th {...thP} col='jun' right>{L2} MTD</Th>
          <Th {...thP} col='proj' right>Proj {L2}</Th>
          <Th {...thP} col='gMJ'  right>{S1}→{S2}</Th>
        </TableRow></TableHead>
        <TableBody>
          {shown.map((r, i) => (
            <TableRow key={i} hover sx={{ '&:hover td': { background: '#F9FAFB' } }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem', color: '#1a1a2e', py: 0.9 }}>{r.name}</TableCell>
              <TableCell sx={{ py: 0.9 }}><SegBadge seg={r.seg} /></TableCell>
              <TableCell sx={{ fontSize: '0.7rem', color: '#9a9ab0', py: 0.9 }}>{r.kam}</TableCell>
              <TableCell align='right' sx={{ fontSize: '0.7rem', color: '#bbb', py: 0.9 }}>{nf(r.apr)}</TableCell>
              <TableCell align='right' sx={{ fontSize: '0.75rem', py: 0.9 }}>{nf(r.may)}</TableCell>
              <TableCell align='right' sx={{ fontSize: '0.75rem', py: 0.9 }}>{nf(r.jun)}</TableCell>
              <TableCell align='right' sx={{ fontSize: '0.78rem', fontWeight: 600, py: 0.9 }}>{nf(r.proj)}</TableCell>
              <TableCell align='right' sx={{ py: 0.9 }}><Pct v={r.gMJ} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
        {shown.length > 1 && (
          <tfoot>
            <TableRow sx={{ '& td': { background: '#F4F6F8', fontWeight: 700, borderTop: `2px solid ${PRIMARY_MID}`, fontSize: '0.75rem', py: 0.9 } }}>
              <TableCell colSpan={3}>Total · {shown.length}</TableCell>
              <TableCell align='right'>{nf(tots.apr)}</TableCell>
              <TableCell align='right'>{nf(tots.may)}</TableCell>
              <TableCell align='right'>{nf(tots.jun)}</TableCell>
              <TableCell align='right'>{nf(tots.proj)}</TableCell>
              <TableCell align='right'><Pct v={tots.may > 0 ? Math.round((tots.proj - tots.may) / tots.may * 100) : null} /></TableCell>
            </TableRow>
          </tfoot>
        )}
      </Table>
    </TableContainer>
  )
}

/* ─── Flag table ─────────────────────────────────────────────────────────── */
function FlagTable({ rows, L0, L1, L2 }) {
  if (!rows.length) return <Box sx={{ py: 3, textAlign: 'center', color: '#bbb', fontSize: '0.78rem' }}>None ✓</Box>
  return (
    <TableContainer sx={{ maxHeight: 320 }}>
      <Table size='small' stickyHeader>
        <TableHead><TableRow>
          {['Account', 'Seg', 'KAM', L0, L1, `${L2} MTD`].map((h, i) => (
            <TableCell key={i} align={i > 2 ? 'right' : 'left'} sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#a0a0b8', background: '#F4F6F8', borderBottom: `2px solid ${PRIMARY_MID}`, py: 1.25 }}>{h}</TableCell>
          ))}
        </TableRow></TableHead>
        <TableBody>
          {rows.slice(0, 50).map((r, i) => (
            <TableRow key={i} hover sx={{ '&:hover td': { background: '#F9FAFB' } }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem', py: 0.9 }}>{r.name}</TableCell>
              <TableCell sx={{ py: 0.9 }}><SegBadge seg={r.seg} /></TableCell>
              <TableCell sx={{ fontSize: '0.7rem', color: '#9a9ab0', py: 0.9 }}>{r.kam}</TableCell>
              <TableCell align='right' sx={{ fontSize: '0.7rem', color: '#bbb', py: 0.9 }}>{nf(r.apr)}</TableCell>
              <TableCell align='right' sx={{ fontSize: '0.75rem', py: 0.9 }}>{nf(r.may)}</TableCell>
              <TableCell align='right' sx={{ fontSize: '0.75rem', py: 0.9 }}>{nf(r.jun)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

/* ─── Watchlist ──────────────────────────────────────────────────────────── */
function WatchTable({ rows, L1, L2, S1, S2, goal }) {
  const sorted = useMemo(() => [...rows].sort((a, b) => (b.may - b.proj) - (a.may - a.proj)), [rows])
  if (!sorted.length) return <Box sx={{ py: 3.5, textAlign: 'center', color: '#bbb', fontSize: '0.8rem' }}>Everyone is on track 🎉</Box>
  return (
    <TableContainer sx={{ maxHeight: 400 }}>
      <Table size='small' stickyHeader>
        <TableHead><TableRow>
          {['Account', 'Seg', 'KAM', 'SAM', `${L1}`, `${L2} MTD`, `Proj`, 'Shortfall', `${S1}→${S2}`, 'Status'].map((h, i) => (
            <TableCell key={i} align={i > 3 ? 'right' : 'left'} sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#a0a0b8', background: '#F4F6F8', borderBottom: `2px solid ${PRIMARY_MID}`, py: 1.25 }}>{h}</TableCell>
          ))}
        </TableRow></TableHead>
        <TableBody>
          {sorted.slice(0, 200).map((r, i) => {
            const sf = r.may - r.proj
            const bg = r.gMJ < 0 ? '#fff8f8' : r.gMJ < goal ? '#fffdf0' : 'inherit'
            const StatusChip = () => r.gMJ >= goal
              ? <Chip label='On track' size='small' sx={{ height: 17, fontSize: '0.58rem', fontWeight: 700, background: '#dcfce7', color: '#166534', borderRadius: '6px' }} />
              : r.gMJ >= 0
                ? <Chip label='At risk' size='small' sx={{ height: 17, fontSize: '0.58rem', fontWeight: 700, background: '#fef9c3', color: '#854d0e', borderRadius: '6px' }} />
                : <Chip label='Behind'  size='small' sx={{ height: 17, fontSize: '0.58rem', fontWeight: 700, background: '#FFE7D9', color: '#B72136', borderRadius: '6px' }} />
            return (
              <TableRow key={i} hover sx={{ background: bg, '&:hover td': { background: '#F9FAFB' } }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem', py: 0.9 }}>{r.name}</TableCell>
                <TableCell sx={{ py: 0.9 }}><SegBadge seg={r.seg} /></TableCell>
                <TableCell sx={{ fontSize: '0.7rem', color: '#9a9ab0', py: 0.9 }}>{r.kam}</TableCell>
                <TableCell sx={{ fontSize: '0.7rem', color: '#9a9ab0', py: 0.9 }}>{displayName(r.sam)}</TableCell>
                <TableCell align='right' sx={{ fontSize: '0.75rem', py: 0.9 }}>{nf(r.may)}</TableCell>
                <TableCell align='right' sx={{ fontSize: '0.75rem', py: 0.9 }}>{nf(r.jun)}</TableCell>
                <TableCell align='right' sx={{ fontSize: '0.75rem', py: 0.9 }}>{nf(r.proj)}</TableCell>
                <TableCell align='right' sx={{ fontSize: '0.78rem', fontWeight: 700, color: RED, py: 0.9 }}>{sf > 0 ? `-${nf(sf)}` : nf(-sf)}</TableCell>
                <TableCell align='right' sx={{ py: 0.9 }}><Pct v={r.gMJ} /></TableCell>
                <TableCell align='right' sx={{ py: 0.9 }}><StatusChip /></TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

/* ─── Potential brands ───────────────────────────────────────────────────── */
function PotTable({ rows, L1, L2, S2 }) {
  const { sortCol, sortDir, toggleSort, sorted, thP } = useSortState('potMonthly')
  const shown = sorted(rows)
  if (!shown.length) return <Box sx={{ py: 3.5, textAlign: 'center', color: '#bbb', fontSize: '0.8rem' }}>No priority brands for this filter.</Box>
  return (
    <TableContainer sx={{ maxHeight: 380 }}>
      <Table size='small' stickyHeader>
        <TableHead><TableRow>
          <Th {...thP} col='name'>Brand</Th>
          <Th>Seg</Th>
          <Th {...thP} col='kam'>KAM</Th>
          <Th {...thP} col='potMonthly' right>Potential / mo</Th>
          <Th {...thP} col='may'        right>{L1}</Th>
          <Th {...thP} col='jun'        right>{L2} MTD</Th>
          <Th {...thP} col='potDrr'     right>Pot. DRR</Th>
          <Th {...thP} col='drrJun'     right>{S2} DRR</Th>
          <Th {...thP} col='potPct'     right>vs Potential</Th>
        </TableRow></TableHead>
        <TableBody>
          {shown.map((r, i) => (
            <TableRow key={i} hover sx={{ '&:hover td': { background: '#F9FAFB' } }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem', py: 0.9 }}>{r.name}</TableCell>
              <TableCell sx={{ py: 0.9 }}><SegBadge seg={r.seg} /></TableCell>
              <TableCell sx={{ fontSize: '0.7rem', color: '#9a9ab0', py: 0.9 }}>{r.kam}</TableCell>
              <TableCell align='right' sx={{ fontSize: '0.78rem', fontWeight: 700, color: PRIMARY, py: 0.9 }}>{nf(r.potMonthly)}</TableCell>
              <TableCell align='right' sx={{ fontSize: '0.75rem', py: 0.9 }}>{nf(r.may)}</TableCell>
              <TableCell align='right' sx={{ fontSize: '0.75rem', py: 0.9 }}>{nf(r.jun)}</TableCell>
              <TableCell align='right' sx={{ fontSize: '0.75rem', fontWeight: 600, py: 0.9 }}>{nf(r.potDrr)}</TableCell>
              <TableCell align='right' sx={{ fontSize: '0.75rem', py: 0.9 }}>{nf(r.drrJun)}</TableCell>
              <TableCell align='right' sx={{ py: 0.9, fontWeight: 700, color: r.potPct >= 100 ? GREEN : RED }}>
                {r.potDrr > 0 ? `${r.potPct}%` : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

/* ─── Section heading ────────────────────────────────────────────────────── */
function SecHead({ title, sub }) {
  return (
    <Box sx={{ mb: 1.25, mt: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 4, height: 16, borderRadius: 1, background: PRIMARY, flexShrink: 0 }} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#1a1a2e' }}>{title}</Typography>
      </Box>
      {sub && <Typography sx={{ fontSize: '0.7rem', color: '#9a9ab0', mt: 0.3, ml: 1.25 }}>{sub}</Typography>}
    </Box>
  )
}

/* ─── Main ───────────────────────────────────────────────────────────────── */
/** Same rule as SalesDashboardService.growthPct — keep the two in step. */
const growthPct = (cur, prev) => (prev > 0 ? Math.round(((cur - prev) / prev) * 100) : (cur > 0 ? 100 : 0))

/**
 * Client-side equivalent of the server's agg(), over an already-filtered set of
 * account rows. Needed because the API only ships ENT/SME aggregates for the
 * whole book and per-KAM — there is no per-SAM (or per-chip) seg breakdown, so
 * those cards had nothing filtered to render and always showed global numbers.
 *
 * dayCounts are recovered from the unfiltered totals rather than assumed: the
 * payload sends DRR but not the divisor, and month lengths differ. Deriving
 * total/DRR is exact enough at these magnitudes (the per-field rounding is <1
 * part in thousands) and avoids re-deriving calendar lengths on the client.
 */
function aggOf(rows, daysL0, daysL1, elapsed) {
  let apr = 0, may = 0, jun = 0, last7 = 0, prev7 = 0, active = 0
  rows.forEach(r => {
    apr += r.apr || 0; may += r.may || 0; jun += r.jun || 0
    last7 += r.last7 || 0; prev7 += r.prev7 || 0
    if ((r.jun || 0) > 0) active++
  })
  const drrApr = apr / daysL0
  const drrMay = may / daysL1
  const drrJun = jun / elapsed
  return {
    accts: rows.length, apr, may, jun, last7, prev7,
    drrApr, drrMay, drrJun,
    gMJ: growthPct(drrJun, drrMay),
    gAJ: growthPct(drrJun, drrApr),
    wow: growthPct(last7, prev7),
    active, inactive: rows.length - active,
  }
}

export default function P2Shipments({ vm, filters, L0, L1, L2, S0, S1, S2 }) {
  const t  = useMemo(() => scopeTotals(vm, filters), [vm, filters])
  const ov = vm.overall
  const DIM = (vm.win && vm.win.dim) || 30

  const allRows  = useMemo(() => (vm.overview.all  || []).filter(r => matchRow(r, filters)), [vm, filters])
  const watchRows = useMemo(() => allRows.filter(r => r.gMJ != null && r.gMJ < filters.goal), [allRows, filters.goal])
  const gainRows  = useMemo(() => (vm.overview.gain || []).filter(r => matchRow(r, filters)), [vm, filters])
  const dropRows  = useMemo(() => (vm.overview.drop || []).filter(r => matchRow(r, filters)), [vm, filters])
  const no3Rows   = useMemo(() => (vm.overview.no3  || []).filter(r => matchRow(r, filters)), [vm, filters])
  const no7Rows   = useMemo(() => (vm.overview.no7  || []).filter(r => matchRow(r, filters)), [vm, filters])
  const zeroRows  = useMemo(() => (vm.overview.zero || []).filter(r => matchRow(r, filters)), [vm, filters])
  const potRows   = useMemo(() => (vm.potential      || []).filter(r => matchRow(r, filters)), [vm, filters])

  // Date labels for the week-on-week heading — the window itself is the same
  // regardless of filter, so these correctly come off the unfiltered overall.
  const { last7 = [], prev7 = [] } = ov
  const mayWk = Math.round((t.drrMay || 0) * 7)

  const isFiltered = filters.seg !== 'ALL' || filters.kam !== 'ALL'
    || filters.sam !== 'ALL' || filters.chips.size > 0

  // ENT vs SME must follow the filter bar like the rest of the page; these two
  // cards used to read vm.overall.ent/.sme, which is the whole book and never
  // changed when a KAM or SAM was picked.
  const segAggs = useMemo(() => {
    const dc = (total, drr, fb) => (drr > 0 ? Math.max(1, Math.round(total / drr)) : fb)
    const daysL0 = dc(ov.apr, ov.drrApr, 30)
    const daysL1 = dc(ov.may, ov.drrMay, 30)
    const elapsed = ov.elapsed || dc(ov.jun, ov.drrJun, 1)
    return {
      ENT: isFiltered ? aggOf(allRows.filter(r => r.seg === 'ENT'), daysL0, daysL1, elapsed) : ov.ent,
      SME: isFiltered ? aggOf(allRows.filter(r => r.seg === 'SME'), daysL0, daysL1, elapsed) : ov.sme,
    }
  }, [allRows, ov, isFiltered])

  // Last-7-day bars, re-totalled for the filtered set. overview.last7Daily is
  // global-only, so the filtered view is rebuilt from the per-merchant daily
  // rows; unfiltered still uses the server's array verbatim.
  const [dailyTrend, setDailyTrend] = useState(null)
  useEffect(() => {
    let cancelled = false
    fetchTrendDaily().then(d => { if (!cancelled) setDailyTrend(d) }).catch(err => console.log(err))
    return () => { cancelled = true }
  }, [])

  const dailyBars = useMemo(() => {
    const server = vm.overview.last7Daily || []
    if (!isFiltered || !dailyTrend) return server
    const dates = dailyTrend.dates || []
    const rows = dailyTrend.rows || []
    const allow = new Set(allRows.map(r => r.oid).filter(Boolean))
    // Nothing to join on (older API without oid) — show the global bars rather
    // than an empty chart that would read as "no shipments".
    if (!allow.size || !dates.length || !rows.length) return server
    const last7Dates = dates.slice(-7)
    const offset = dates.length - last7Dates.length
    return last7Dates.map((date, i) => {
      let total = 0
      rows.forEach(r => {
        if (!allow.has(r.merchantOid)) return
        total += (r.daily || [])[offset + i] || 0
      })
      const d = new Date(date + 'T00:00:00')
      return { label: `${d.getDate()}-${d.toLocaleString('en', { month: 'short' })}`, total }
    })
  }, [vm, dailyTrend, allRows, isFiltered])

  return (
    <Box>
      {/* KPI strip */}
      <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
        {[
          { label: `${L2} DRR`,        value: nf(t.drrJun),     sub: 'shipments / day',           color: PRIMARY, icon: SpeedIcon },
          { label: 'Prior-month DRR',   value: nf(t.drrMay),     sub: L1,                           color: '#555' },
          { label: `${S1}→${S2}`,      value: <Pct v={t.gMJ} size='inherit' />, sub: 'projected',                color: t.gMJ >= 0 ? GREEN : RED },
          { label: `${S0}→${S2}`,      value: <Pct v={t.gAJ} size='inherit' />, sub: 'projected',                color: t.gAJ >= 0 ? GREEN : RED },
          { label: 'Week-on-week',      value: <Pct v={t.wow} size='inherit' />, sub: 'last 7d vs prior 7d',      color: t.wow >= 0 ? GREEN : RED, icon: t.wow >= 0 ? TrendingUpIcon : TrendingDownIcon },
        ].map((k, i) => (
          <Grid item xs={6} sm={4} md={2.4} key={i}><Kpi {...k} /></Grid>
        ))}
      </Grid>

      {/* ENT vs SME */}
      <SecHead title='Daily Run Rate — Enterprise vs SME' sub={`DRR = month shipments ÷ days elapsed (${ov.elapsed} days for ${L2})`} />
      <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
        {[{ label: 'Enterprise (ENT)', seg: 'ENT', data: segAggs.ENT }, { label: 'SME', seg: 'SME', data: segAggs.SME }].map(({ label, seg, data }) => (
          <Grid item xs={12} md={6} key={seg}>
            <Box sx={{ background: '#fff', borderRadius: '12px', border: '1px solid rgba(145,158,171,0.24)', boxShadow: 'none', overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid rgba(145,158,171,0.24)', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', flex: 1 }}>{label}</Typography>
                <SegBadge seg={seg} />
              </Box>
              <DrrInfo t={data} S0={S0} S1={S1} S2={S2} L0={L0} L1={L1} L2={L2} />
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Daily 7-day */}
      <SecHead title='Last 7 Days — Daily Shipment Volume' sub='Most recent day highlighted in orange' />
      <SCard title=''>
        <DailyBars data={dailyBars} />
      </SCard>

      {/* MoM growth */}
      <SecHead title='Month-on-Month Growth' />
      <SCard title=''>
        <Box sx={{ p: 1.5 }}>
          <Table size='small'>
            <TableHead><TableRow>
              {['Basis', 'Shipments', 'DRR', `vs projected ${L2}`].map((h, i) => (
                <TableCell key={i} align={i > 0 ? 'right' : 'left'} sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#a0a0b8', background: '#F4F6F8', borderBottom: `2px solid ${PRIMARY_MID}`, py: 1.1 }}>{h}</TableCell>
              ))}
            </TableRow></TableHead>
            <TableBody>
              {[
                { label: `${L0} (full)`,           ships: t.apr,  drr: t.drrApr, pct: t.gAJ, hl: false },
                { label: `${L1} (full)`,           ships: t.may,  drr: t.drrMay, pct: t.gMJ, hl: false },
                { label: `${L2} projected ×${DIM}`, ships: t.proj, drr: t.drrJun, pct: null,  hl: true  },
                { label: `${L2} MTD`,              ships: t.jun,  drr: t.drrJun, pct: null,  hl: false, sub: `${ov.elapsed} days` },
              ].map((r, i) => (
                <TableRow key={i} sx={{ background: r.hl ? PRIMARY_LIGHT : 'inherit', '&:hover td': { background: r.hl ? '#EFF6FF' : '#F9FAFB' } }}>
                  <TableCell sx={{ fontSize: '0.78rem', fontWeight: r.hl ? 700 : 500, py: 1 }}>{r.label}</TableCell>
                  <TableCell align='right' sx={{ fontSize: '0.78rem', fontWeight: r.hl ? 700 : 400, py: 1 }}>{nf(r.ships)}</TableCell>
                  <TableCell align='right' sx={{ fontSize: '0.78rem', fontWeight: r.hl ? 700 : 400, py: 1 }}>{nf(r.drr)}</TableCell>
                  <TableCell align='right' sx={{ py: 1 }}>{r.pct != null ? <Pct v={r.pct} /> : <span style={{ fontSize: '0.7rem', color: '#bbb' }}>{r.sub || '—'}</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </SCard>

      {/* WoW */}
      <SecHead title='Week-on-Week' sub={`Last 7 days (${last7.join(', ')}) vs previous 7 (${prev7.join(', ')})`} />
      <SCard title=''>
        <Box sx={{ p: 1.5 }}>
          <Table size='small'>
            <TableHead><TableRow>
              {['Window', 'Shipments', 'Per day', 'Change'].map((h, i) => (
                <TableCell key={i} align={i > 0 ? 'right' : 'left'} sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#a0a0b8', background: '#F4F6F8', borderBottom: `2px solid ${PRIMARY_MID}`, py: 1.1 }}>{h}</TableCell>
              ))}
            </TableRow></TableHead>
            <TableBody>
              {[
                { label: 'Last 7 days',          ships: t.last7, pd: Math.round((t.last7 || 0) / 7), pct: t.wow },
                { label: 'Previous 7 days',      ships: t.prev7, pd: Math.round((t.prev7 || 0) / 7), pct: null  },
                { label: `${L1} weekly average`, ships: mayWk,   pd: t.drrMay, pct: null },
              ].map((r, i) => (
                <TableRow key={i} hover sx={{ '&:hover td': { background: '#F9FAFB' } }}>
                  <TableCell sx={{ fontSize: '0.78rem', py: 1 }}>{r.label}</TableCell>
                  <TableCell align='right' sx={{ fontSize: '0.78rem', py: 1 }}>{nf(r.ships)}</TableCell>
                  <TableCell align='right' sx={{ fontSize: '0.78rem', py: 1 }}>{nf(r.pd)}</TableCell>
                  <TableCell align='right' sx={{ py: 1 }}>{r.pct != null ? <Pct v={r.pct} /> : <span style={{ fontSize: '0.7rem', color: '#bbb' }}>baseline</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </SCard>

      {/* Watchlist */}
      <SecHead title='Needs Attention' sub={`Accounts below ${filters.goal}% monthly growth goal — sorted by shortfall size`} />
      <SCard title='Watchlist' badge={watchRows.length} cnt={`${Math.min(watchRows.length, 200)} shown · goal ${filters.goal}%`}>
        <WatchTable rows={watchRows} L1={L1} L2={L2} S1={S1} S2={S2} goal={filters.goal} />
      </SCard>

      {/* Potential */}
      <SecHead title='Potential Brands' sub='Flagged as Priority. Current vs Potential = current DRR ÷ Potential DRR.' />
      <SCard title='Priority accounts' badge={potRows.length}>
        <PotTable rows={potRows} L1={L1} L2={L2} S2={S2} />
      </SCard>

      {/* Movers */}
      <SecHead title='Movers — projected vs prior month' />
      <Grid container spacing={1.75} sx={{ mb: 2 }}>
        <Grid item xs={12} lg={6}>
          <SCard title='Top Gainers ▲' badge={<Chip label='Growing' size='small' sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, background: '#dcfce7', color: '#166534', borderRadius: '6px' }} />} cnt={`${Math.min(gainRows.length, 50)} shown`}>
            <AccTable rows={gainRows} L0={L0} L1={L1} L2={L2} S1={S1} S2={S2} />
          </SCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <SCard title='Top Droppers ▼' badge={<Chip label='Declining' size='small' sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, background: '#FFE7D9', color: '#B72136', borderRadius: '6px' }} />} cnt={`${Math.min(dropRows.length, 50)} shown`}>
            <AccTable rows={dropRows} L0={L0} L1={L1} L2={L2} S1={S1} S2={S2} />
          </SCard>
        </Grid>
      </Grid>

      {/* Red flags */}
      <SecHead title='Red Flags — accounts that stopped shipping' sub='Only accounts active last month are shown.' />
      <SCard title='No shipments · last 3 days' badge={no3Rows.length}><FlagTable rows={no3Rows} L0={L0} L1={L1} L2={L2} /></SCard>
      <SCard title='No shipments · last 7 days' badge={no7Rows.length}><FlagTable rows={no7Rows} L0={L0} L1={L1} L2={L2} /></SCard>
      <SCard title='Zero shipments this month'  badge={zeroRows.length}><FlagTable rows={zeroRows} L0={L0} L1={L1} L2={L2} /></SCard>
    </Box>
  )
}
